import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { formLabelClass, fieldErrorClass } from './onboarding-layout';

const emailSchema = z.email('Invalid email address');

export interface OnboardingMultiEmailInputProps {
  label: string;
  value: string[];
  onChange: (emails: string[]) => void;
  error?: string;
  placeholder?: string;
  collapseAfterFirstLine?: boolean;
}

type EmailChipProps = {
  email: string;
  onRemove: () => void;
};

function EmailChip({ email, onRemove }: EmailChipProps) {
  return (
    <span
      data-email-chip
      className="inline-flex max-w-full items-center gap-1 rounded-2xl border border-solid border-[var(--inv-accent)] bg-[var(--inv-accent)] py-0.5 pl-2.5 pr-1.5 text-[0.8rem]">
      <span className="truncate leading-snug text-white">{email}</span>
      <button
        type="button"
        className="flex shrink-0 cursor-pointer items-center border-0 bg-transparent p-0 text-[1.1rem] leading-none text-white/85 hover:bg-transparent hover:text-white focus-visible:outline-none"
        aria-label={`Remove ${email}`}
        onClick={onRemove}>
        ×
      </button>
    </span>
  );
}

function OnboardingMultiEmailInput({
  label,
  value,
  onChange,
  error,
  placeholder = 'Enter reviewer email…',
  collapseAfterFirstLine = false,
}: OnboardingMultiEmailInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstLineCount, setFirstLineCount] = useState(value.length);
  const measureRef = useRef<HTMLDivElement>(null);

  const measureFirstLine = useCallback(() => {
    if (!collapseAfterFirstLine || value.length === 0) {
      setFirstLineCount(value.length);
      return;
    }

    const container = measureRef.current;
    if (!container) return;

    const chips = container.querySelectorAll('[data-email-chip]');
    if (chips.length === 0) {
      setFirstLineCount(value.length);
      return;
    }

    const firstTop = (chips[0] as HTMLElement).offsetTop;
    let count = chips.length;
    for (let i = 1; i < chips.length; i++) {
      if ((chips[i] as HTMLElement).offsetTop > firstTop + 1) {
        count = i;
        break;
      }
    }
    setFirstLineCount(count);
  }, [collapseAfterFirstLine, value.length]);

  useLayoutEffect(() => {
    measureFirstLine();
  }, [measureFirstLine, value]);

  useEffect(() => {
    if (!collapseAfterFirstLine || !measureRef.current?.parentElement) return;

    const observer = new ResizeObserver(() => {
      measureFirstLine();
    });
    observer.observe(measureRef.current.parentElement);
    return () => observer.disconnect();
  }, [collapseAfterFirstLine, measureFirstLine]);

  useEffect(() => {
    if (value.length <= firstLineCount) {
      setExpanded(false);
    }
  }, [value.length, firstLineCount]);

  const tryAddEmail = (raw: string) => {
    const email = raw.trim().replace(/,\s*$/, '');
    if (!email) return;
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setInputError(result.error.issues[0]?.message ?? 'Invalid email address');
      return;
    }
    if (value.includes(email)) {
      setInputError('Email already added');
      return;
    }
    onChange([...value, email]);
    setInputValue('');
    setInputError('');
  };

  const removeEmail = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleChange = (val: string) => {
    if (val.includes(',')) {
      const parts = val.split(',');
      const remainder = parts.pop() ?? '';
      parts.forEach(part => tryAddEmail(part));
      setInputValue(remainder.trimStart());
      if (inputError) setInputError('');
      return;
    }
    setInputValue(val);
    if (inputError) setInputError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      tryAddEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue) {
      onChange(value.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) tryAddEmail(inputValue);
  };

  const displayError = inputError || error;
  const hiddenCount = Math.max(0, value.length - firstLineCount);
  const showToggle = collapseAfterFirstLine && hiddenCount > 0;
  const visibleEntries =
    collapseAfterFirstLine && !expanded
      ? value.slice(0, firstLineCount).map((email, index) => ({ email, index }))
      : value.map((email, index) => ({ email, index }));

  return (
    <div className="flex flex-col gap-2" onKeyDown={handleKeyDown}>
      {value.length > 0 && (
        <div className="relative w-full">
          {collapseAfterFirstLine ? (
            <div
              ref={measureRef}
              className="pointer-events-none absolute inset-x-0 top-0 flex flex-wrap gap-1.5 opacity-0"
              aria-hidden>
              {value.map((email, i) => (
                <EmailChip
                  key={`measure-${email}-${i}`}
                  email={email}
                  onRemove={() => undefined}
                />
              ))}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-1.5">
            {visibleEntries.map(({ email, index }) => (
              <EmailChip
                key={`${email}-${index}`}
                email={email}
                onRemove={() => removeEmail(index)}
              />
            ))}
          </div>
          {showToggle ? (
            <button
              type="button"
              className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2.5 py-0.5 text-[0.75rem] font-medium text-[var(--inv-text-muted)] hover:bg-[var(--inv-surface-muted)]"
              onClick={() => setExpanded(prev => !prev)}>
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />+{hiddenCount} more
                </>
              )}
            </button>
          ) : null}
        </div>
      )}
      {label ? <label className={formLabelClass}>{label}</label> : null}
      <Input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={e => handleChange(e.target.value)}
        onBlur={handleBlur}
        className={cn(displayError && 'border-[#fecdca] focus-visible:ring-[#fecdca]')}
      />
      {displayError && (
        <p role="alert" className={fieldErrorClass}>
          {displayError}
        </p>
      )}
    </div>
  );
}

export { OnboardingMultiEmailInput };
